import type {
  ProductSearchFilterCategory,
  ProductSearchFilterFacet,
  ProductSearchFilterTypeKeys,
  ProductSearchFiltersViewModel,
  SearchFilterFacet,
  SliderFilterKeys,
} from '@jane/bloom/viewModels/productSearchFilters';

import { useBloomFilters } from './useBloomFilters';

export const FilterCategoryLabel: Record<
  keyof ProductSearchFiltersViewModel | 'potencies',
  string
> = {
  activities: 'Activity',
  brands: 'Brand',
  brandSpecials: 'Jane Gold',
  categories: 'Category',
  cbdPotencies: 'CBD potency',
  feelings: 'Feeling',
  lineages: 'Lineage',
  potencies: 'Potency',
  prices: 'Price',
  ratings: 'Rating',
  sale: 'Sale',
  specials: 'Deal',
  terpenes: 'Top terpenes',
  thcPotencies: 'THC potency',
  weights: 'Weight',
};

/** The `has_brand_discount` facet buckets products under `true` when they carry a Jane Gold discount. */
const JANE_GOLD_FACET_VALUE = 'true';

export function useProductSearchFilters() {
  const { availableFilters, selectedCategory } = useBloomFilters();

  /**
   * The Jane Gold option, carrying its count for the current result set.
   *
   * Always resolvable: the `has_brand_discount` facet is fetched against the
   * active search criteria, so filtering along another facet down to no Gold
   * products drops the bucket entirely rather than reporting zero. Defaulting the
   * count here keeps the filter on screen and disabled instead of disappearing.
   * Whether to offer it at all is `shouldShowGold`, not this count.
   */
  const janeGoldFilterOption: ProductSearchFilterFacet = {
    count:
      availableFilters?.brandSpecials.find(
        (facet) => facet.value === JANE_GOLD_FACET_VALUE
      )?.count ?? 0,
    label: FilterCategoryLabel.brandSpecials,
    value: JANE_GOLD_FACET_VALUE,
  };

  const getProductSearchFilterOptions = (
    filter: keyof ProductSearchFiltersViewModel | 'ratings'
  ) => {
    return availableFilters?.[filter] || [];
  };

  const getProductSubcategories = (selectedCategory: string) => {
    const cat = availableFilters?.['categories'].find(
      (category) => category.value === selectedCategory
    )?.subCategories;

    return cat || [];
  };

  const getBooleanFilterKeysNonEmpty = () => {
    return Object.entries(availableFilters || {})
      .filter(([key, val]) => {
        return !key.toLowerCase().includes('potencies') && val.length > 0;
      })
      .map(([key, _]) => key as ProductSearchFilterTypeKeys);
  };

  const getPotencyFilterKeysNonEmpty = () => {
    return Object.entries(availableFilters || {})
      .filter(([key, val]) => {
        return key.toLowerCase().includes('potencies') && val.length > 0;
      })
      .map(([key, _]) => key as SliderFilterKeys);
  };

  const getProductSearchFilterOptionLabel = (
    filter: keyof ProductSearchFiltersViewModel,
    filterOption: string
  ) => {
    let category = availableFilters?.[filter] as SearchFilterFacet[];

    if (filter === 'categories') {
      const [rootFilterOption, subFilterOption] = filterOption.split(':');

      if (subFilterOption) {
        const item = (category || []).find(
          (option) => option.value === rootFilterOption
        ) as ProductSearchFilterCategory;

        return item?.subCategories?.find((s) => s.value === filterOption)
          ?.label;
      }

      // `sale` is filtered through the category param but lives outside the
      // categories list, so its label has to be looked up alongside them.
      category = [...(category || []), ...(availableFilters?.sale || [])];
    }

    return (
      (category || []).find((option) => option.value === filterOption)?.label ||
      filterOption
    );
  };

  return {
    getBooleanFilterKeysNonEmpty,
    getPotencyFilterKeysNonEmpty,
    getProductSearchFilterOptionLabel,
    getProductSearchFilterOptions,
    getProductSubcategories,
    janeGoldFilterOption,
    productSearchFilters: availableFilters,
    selectedCategory,
  };
}
